import React from 'react';
import { Link } from 'react-router-dom';
import PolicyLayout from './PolicyLayout';

const PrivacyPolicy = () => {
  return (
    <PolicyLayout title="Privacy Policy" lastUpdated="January 20, 2026">
      <p>
        AppealBot ("we," "us," or "our") is committed to protecting your privacy. This Privacy
        Policy explains how we collect, use, disclose, and safeguard your information when you
        use our platform.
      </p>

      <h2>1. Information We Collect</h2>

      <h3>1.1 Information You Provide</h3>
      <p>We collect information you directly provide, including:</p>
      <ul>
        <li><strong>Account Information:</strong> Name, email address, phone number, practice name, and professional credentials</li>
        <li><strong>Billing Information:</strong> Payment card details, billing address, and transaction history</li>
        <li><strong>Claims Data:</strong> Patient information, insurance claims, denial reasons, and appeal documentation</li>
        <li><strong>Communications:</strong> Messages, support requests, and feedback you send us</li>
      </ul>

      <h3>1.2 Automatically Collected Information</h3>
      <p>When you use AppealBot, we automatically collect:</p>
      <ul>
        <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers</li>
        <li><strong>Usage Data:</strong> Pages visited, features used, and interaction patterns</li>
        <li><strong>Log Data:</strong> IP addresses, access times, and referring URLs</li>
        <li><strong>Cookies:</strong> See our <Link to="/cookie-policy">Cookie Policy</Link> for details</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>We use collected information to:</p>
      <ul>
        <li>Provide, maintain, and improve our services</li>
        <li>Process claims and generate appeal documents</li>
        <li>Process payments and manage subscriptions</li>
        <li>Send transactional communications and service updates</li>
        <li>Respond to support requests and inquiries</li>
        <li>Analyze usage patterns to enhance user experience</li>
        <li>Detect, prevent, and address security issues</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h2>3. Protected Health Information (PHI)</h2>
      <p>
        As a healthcare technology provider, we handle Protected Health Information in accordance
        with HIPAA regulations. Our handling of PHI is governed by:
      </p>
      <ul>
        <li>Our Business Associate Agreement with healthcare providers</li>
        <li>The HIPAA Privacy and Security Rules</li>
        <li>State health information privacy laws</li>
      </ul>
      <p>
        We implement administrative, physical, and technical safeguards to protect PHI from
        unauthorized access, use, or disclosure.
      </p>

      <h2>4. Information Sharing</h2>
      <p>We may share your information with:</p>

      <h3>4.1 Service Providers</h3>
      <p>
        Third-party vendors who assist in operating our platform, including cloud hosting,
        payment processing, and analytics providers. These providers are bound by confidentiality
        obligations.
      </p>

      <h3>4.2 Business Partners</h3>
      <p>
        Clearinghouses and payer integrations you authorize to submit claims and appeals on
        your behalf.
      </p>

      <h3>4.3 Legal Requirements</h3>
      <p>
        We may disclose information when required by law, court order, or government request,
        or to protect our rights, privacy, safety, or property.
      </p>

      <h3>4.4 Business Transfers</h3>
      <p>
        In connection with a merger, acquisition, or sale of assets, your information may be
        transferred to the acquiring entity.
      </p>

      <h2>5. Data Security</h2>
      <p>We implement robust security measures including:</p>
      <ul>
        <li>AES-256 encryption for data at rest</li>
        <li>TLS 1.2+ encryption for data in transit</li>
        <li>Multi-factor authentication options</li>
        <li>Role-based access controls</li>
        <li>Regular security audits and penetration testing</li>
        <li>Employee training on data protection</li>
      </ul>
      <p>
        While we strive to protect your information, no method of transmission or storage is
        100% secure. We cannot guarantee absolute security.
      </p>

      <h2>6. Data Retention</h2>
      <p>
        We retain your information for as long as necessary to provide our services and comply
        with legal obligations. See our <Link to="/data-retention">Data Retention Policy</Link> for
        specific retention periods.
      </p>

      <h2>7. Your Rights and Choices</h2>
      <p>Depending on your location, you may have the right to:</p>
      <ul>
        <li><strong>Access:</strong> Request a copy of your personal information</li>
        <li><strong>Correction:</strong> Request correction of inaccurate information</li>
        <li><strong>Deletion:</strong> Request deletion of your information (subject to legal requirements)</li>
        <li><strong>Portability:</strong> Receive your data in a portable format</li>
        <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
        <li><strong>Restriction:</strong> Request limitation of certain processing activities</li>
      </ul>
      <p>
        To exercise these rights, contact us at privacy@appealbot.com.
      </p>

      <h2>8. California Privacy Rights</h2>
      <p>
        California residents have additional rights under the California Consumer Privacy Act (CCPA):
      </p>
      <ul>
        <li>Right to know what personal information is collected and how it's used</li>
        <li>Right to delete personal information</li>
        <li>Right to opt-out of the sale of personal information (we do not sell personal data)</li>
        <li>Right to non-discrimination for exercising privacy rights</li>
      </ul>

      <h2>9. Children's Privacy</h2>
      <p>
        AppealBot is not intended for individuals under 18 years of age. We do not knowingly
        collect personal information from children. If we learn we have collected information
        from a child, we will delete it promptly.
      </p>

      <h2>10. International Users</h2>
      <p>
        AppealBot is operated in the United States and is intended for users in the United States.
        If you access our services from outside the US, your information may be transferred to
        and processed in the United States.
      </p>

      <h2>11. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy periodically. We will notify you of material changes
        via email or through our platform. The "Last Updated" date indicates when changes were
        made.
      </p>

      <h2>12. Contact Us</h2>
      <p>
        For questions or concerns about this Privacy Policy or our data practices:
      </p>
      <ul>
        <li>Email: privacy@appealbot.com</li>
        <li>Privacy Officer: AppealBot, Inc.</li>
      </ul>
    </PolicyLayout>
  );
};

export default PrivacyPolicy;
